// Arithmetic Calculators helpers: classic "long" column arithmetic
// (division/multiplication/addition/subtraction) with step-by-step work
// shown, plus GCD/LCM, prime factorization, prime checking, modulo,
// divisibility, digit sum/digital root, and rounding. Pure elementary/
// number-theory arithmetic - distinct from lib/tools/equationsExpressionsCalculators.ts
// (algebra) and lib/tools/powersRootsCalculators.ts (exponents/radicals).

function parseNumberList(raw: unknown): number[] {
    return String(raw ?? '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v))
}

// ============================================================
// 1. Long Division Calculator
// ============================================================
export function longDivisionCalculator(params: { dividend: unknown; divisor: unknown }): {
    quotient: number; remainder: number; decimal_result: number; steps: string
} {
    const dividend = Math.max(Math.round(Number(params.dividend)) || 0, 0)
    const divisor = Math.max(Math.round(Number(params.divisor)) || 1, 1)

    const dividendStr = String(dividend)
    const lines: string[] = []
    let currentRemainder = 0
    let quotientDigits = ''
    for (let i = 0; i < dividendStr.length; i++) {
        const digit = Number(dividendStr[i])
        const bringDown = currentRemainder * 10 + digit
        const qDigit = Math.floor(bringDown / divisor)
        const newRemainder = bringDown - qDigit * divisor
        if (quotientDigits.length > 0 || qDigit > 0) {
            quotientDigits += qDigit
        }
        lines.push(`Bring down ${digit} → ${bringDown}; ${bringDown} ÷ ${divisor} = ${qDigit} remainder ${newRemainder}`)
        currentRemainder = newRemainder
    }
    if (quotientDigits === '') quotientDigits = '0'

    return {
        quotient: Number(quotientDigits),
        remainder: currentRemainder,
        decimal_result: dividend / divisor,
        steps: lines.join('\n'),
    }
}

// ============================================================
// 2. Long Multiplication Calculator
// ============================================================
export function longMultiplicationCalculator(params: { number1: unknown; number2: unknown }): {
    product: number; steps: string
} {
    const a = Math.max(Math.round(Number(params.number1)) || 0, 0)
    const b = Math.max(Math.round(Number(params.number2)) || 0, 0)

    const bStr = String(b)
    const lines: string[] = []
    let total = 0
    for (let i = bStr.length - 1; i >= 0; i--) {
        const digit = Number(bStr[bStr.length - 1 - i])
        const placeValue = Math.pow(10, i)
        const partial = a * digit * placeValue
        lines.push(`${a} × ${digit} (place value ${placeValue}) = ${partial}`)
        total += partial
    }
    lines.push(`Sum of partial products = ${total}`)

    return { product: total, steps: lines.join('\n') }
}

// ============================================================
// 3. Long Addition Calculator
// ============================================================
export function longAdditionCalculator(params: { numbers_list: unknown }): { sum: number; steps: string } {
    const numbers = parseNumberList(params.numbers_list)
        .map((n) => Math.max(Math.round(n), 0))
    if (numbers.length === 0) return { sum: 0, steps: '' }

    const maxLen = Math.max(...numbers.map((n) => String(n).length))
    const padded = numbers.map((n) => String(n).padStart(maxLen, '0'))
    let carry = 0
    const resultDigits: number[] = []
    const lines: string[] = []
    for (let col = maxLen - 1; col >= 0; col--) {
        let colSum = carry
        for (const p of padded) colSum += Number(p[col])
        const digit = colSum % 10
        const newCarry = Math.floor(colSum / 10)
        resultDigits.unshift(digit)
        lines.push(`Column ${maxLen - col}: sum = ${colSum} → digit ${digit}, carry ${newCarry}`)
        carry = newCarry
    }
    if (carry > 0) resultDigits.unshift(carry)

    return { sum: Number(resultDigits.join('')), steps: lines.join('\n') }
}

// ============================================================
// 4. Long Subtraction Calculator
// ============================================================
export function longSubtractionCalculator(params: { number1: unknown; number2: unknown }): {
    difference: number; steps: string
} {
    let a = Math.max(Math.round(Number(params.number1)) || 0, 0)
    let b = Math.max(Math.round(Number(params.number2)) || 0, 0)
    let negative = false
    if (a < b) {
        ;[a, b] = [b, a]
        negative = true
    }

    const maxLen = Math.max(String(a).length, String(b).length)
    const aDigits = String(a).padStart(maxLen, '0').split('').map(Number)
    const bDigits = String(b).padStart(maxLen, '0').split('').map(Number)
    let borrow = 0
    const resultDigits: number[] = []
    const lines: string[] = []
    for (let col = maxLen - 1; col >= 0; col--) {
        let top = aDigits[col] - borrow
        const bot = bDigits[col]
        if (top < bot) {
            top += 10
            borrow = 1
        } else {
            borrow = 0
        }
        const digit = top - bot
        resultDigits.unshift(digit)
        lines.push(`Column ${maxLen - col}: ${top} − ${bot} = ${digit}${borrow ? ' (borrowed)' : ''}`)
    }

    const difference = Number(resultDigits.join(''))
    return { difference: negative ? -difference : difference, steps: lines.join('\n') }
}

// ============================================================
// 5. GCD (Greatest Common Divisor) Calculator
// ============================================================
function gcdTwo(a: number, b: number): number {
    return b === 0 ? a : gcdTwo(b, a % b)
}
export function gcdCalculator(params: { numbers_list: unknown }): { gcd: number } {
    const numbers = parseNumberList(params.numbers_list).map((n) => Math.abs(Math.round(n))).filter((n) => n > 0)
    if (numbers.length === 0) return { gcd: 0 }
    return { gcd: numbers.reduce((g, n) => gcdTwo(g, n)) }
}

// ============================================================
// 6. LCM (Least Common Multiple) Calculator
// ============================================================
function lcmTwo(a: number, b: number): number {
    return Math.abs(a * b) / gcdTwo(a, b)
}
export function lcmCalculator(params: { numbers_list: unknown }): { lcm: number } {
    const numbers = parseNumberList(params.numbers_list).map((n) => Math.abs(Math.round(n))).filter((n) => n > 0)
    if (numbers.length === 0) return { lcm: 0 }
    return { lcm: numbers.reduce((l, n) => lcmTwo(l, n)) }
}

// ============================================================
// 7. Prime Factorization Calculator
// ============================================================
export function primeFactorizationCalculator(params: { number: unknown }): {
    prime_factorization: string; factor_count: number
} {
    let num = Math.max(Math.round(Number(params.number)) || 2, 2)
    const factors: Array<[number, number]> = []
    for (let p = 2; p * p <= num; p++) {
        let count = 0
        while (num % p === 0) {
            num /= p
            count++
        }
        if (count > 0) factors.push([p, count])
    }
    if (num > 1) factors.push([num, 1])

    const superscripts: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' }
    const toSuperscript = (n: number) => String(n).split('').map((d) => superscripts[d]).join('')
    const text = factors.map(([base, exp]) => (exp > 1 ? `${base}${toSuperscript(exp)}` : String(base))).join(' × ')

    return { prime_factorization: text, factor_count: factors.length }
}

// ============================================================
// 8. Prime Number Checker
// ============================================================
export function primeNumberChecker(params: { number: unknown }): { is_prime: string; smallest_factor: string } {
    const n = Math.round(Number(params.number)) || 0
    if (n < 2) return { is_prime: 'No', smallest_factor: '—' }
    for (let i = 2; i * i <= n; i++) {
        if (n % i === 0) return { is_prime: 'No', smallest_factor: String(i) }
    }
    return { is_prime: 'Yes', smallest_factor: '—' }
}

// ============================================================
// 9. Modulo Calculator
// ============================================================
export function moduloCalculator(params: { dividend: unknown; divisor: unknown }): { remainder: number; quotient: number } {
    const a = Math.round(Number(params.dividend)) || 0
    const n = Math.round(Number(params.divisor)) || 1
    const remainder = ((a % n) + n) % n
    const quotient = (a - remainder) / n
    return { remainder, quotient }
}

// ============================================================
// 10. Divisibility Checker
// ============================================================
export function divisibilityChecker(params: { number: unknown }): { divisible_by: string } {
    const n = Math.round(Number(params.number)) || 0
    const divisors = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    const matches = divisors.filter((d) => n % d === 0)
    return { divisible_by: matches.length > 0 ? matches.join(', ') : 'None (2–12)' }
}

// ============================================================
// 11. Digit Sum / Digital Root Calculator
// ============================================================
function digitSumOf(n: number): number {
    return String(Math.abs(n)).split('').reduce((s, d) => s + Number(d), 0)
}
export function digitSumCalculator(params: { number: unknown }): { digit_sum: number; digital_root: number } {
    const n = Math.round(Number(params.number)) || 0
    const digitSum = digitSumOf(n)
    let root = Math.abs(n)
    while (root >= 10) root = digitSumOf(root)
    return { digit_sum: digitSum, digital_root: root }
}

// ============================================================
// 12. Rounding Calculator
// ============================================================
export function roundingCalculator(params: { number: unknown; decimal_places: unknown }): { rounded_result: number } {
    const value = Number(params.number) || 0
    const decimalPlaces = Math.round(Number(params.decimal_places)) || 0
    const factor = Math.pow(10, decimalPlaces)
    return { rounded_result: Math.round(value * factor) / factor }
}
